"use client";

import { useEffect, useId, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import {
  LOCATION_FIELDS,
  LOCATION_INPUT_CLASS_NAME,
  LOCATION_SEARCH_DELAY_MS,
  LOCATION_SEARCH_MIN_LENGTH,
  LOCATION_SEARCHING_MESSAGE,
  LOCATION_SINGLE_MAX_LENGTH,
} from "./location-input.constants";
import type {
  LocationInputProps,
  LocationSuggestion,
} from "./location-input.types";

export function LocationInput(props: LocationInputProps) {
  const {
    onResolvingChange,
    provider,
    labelClassName,
    controlClassName,
    disabled,
  } = props;
  const id = useId();
  const revision = useRef(0);
  const [query, setQuery] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [status, setStatus] = useState("");
  const [resolving, setResolving] = useState(false);

  const closeSuggestions = () => {
    setQuery(null);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const dismiss = () => {
    revision.current += 1;
    closeSuggestions();
    setStatus("");
    setResolving(false);
    onResolvingChange?.(false);
  };

  useEffect(() => {
    if (query === null || disabled) return;
    const request = ++revision.current;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setStatus(LOCATION_SEARCHING_MESSAGE);
      try {
        const results = await provider.search(query);
        if (cancelled || request !== revision.current) return;
        setSuggestions(results);
        setStatus(
          results.length
            ? ""
            : "No matches. You can enter your location manually.",
        );
      } catch {
        if (!cancelled && request === revision.current)
          setStatus(
            "Suggestions unavailable. You can enter your location manually.",
          );
      }
    }, LOCATION_SEARCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
      cancelled = true;
    };
  }, [query, provider, disabled]);

  // Invalidate pending place details when the editor closes or is unmounted.
  useEffect(
    () => () => {
      revision.current += 1;
    },
    [],
  );

  const select = async (suggestion: LocationSuggestion) => {
    const request = ++revision.current;
    closeSuggestions();
    setResolving(true);
    onResolvingChange?.(true);
    setStatus("Loading selected location...");
    try {
      const location = await provider.resolve(suggestion.id);
      if (request !== revision.current) return;
      if (props.mode === "single") props.onChange(suggestion.label);
      else props.onChange(location);
      dismiss();
    } catch {
      if (request === revision.current) {
        setStatus("Could not load this location. You can enter it manually.");
        setResolving(false);
        onResolvingChange?.(false);
      }
    }
  };

  return (
    <div className="space-y-3">
      {(props.mode === "single"
        ? LOCATION_FIELDS.slice(0, 1)
        : LOCATION_FIELDS
      ).map((field) => (
        <div key={field.key} className="space-y-1.5">
          <label
            htmlFor={`${id}-${field.key}`}
            className={cn(
              "block text-sm font-semibold text-[#344054]",
              labelClassName,
            )}
          >
            {props.mode === "single" ? props.label || "Location" : field.label}
          </label>
          <div className="relative">
            <input
              id={`${id}-${field.key}`}
              className={cn(LOCATION_INPUT_CLASS_NAME, controlClassName)}
              value={
                props.mode === "single"
                  ? props.value
                  : props.value[field.key] || ""
              }
              disabled={disabled}
              maxLength={
                props.mode === "single"
                  ? props.maxLength || LOCATION_SINGLE_MAX_LENGTH
                  : field.maxLength
              }
              autoComplete="off"
              {...(field.key === "city"
                ? {
                    role: "combobox",
                    "aria-autocomplete": "list" as const,
                    "aria-expanded": suggestions.length > 0 || Boolean(status),
                    "aria-controls":
                      suggestions.length || status
                        ? `${id}-suggestions`
                        : undefined,
                    "aria-activedescendant":
                      activeIndex >= 0
                        ? `${id}-option-${activeIndex}`
                        : undefined,
                  }
                : {})}
              onChange={(event) => {
                dismiss();
                // A manual edit cannot retain the previous place's coordinates.
                if (props.mode === "single") props.onChange(event.target.value);
                else
                  props.onChange({
                    city: props.value.city,
                    state: props.value.state,
                    country: props.value.country,
                    [field.key]: event.target.value,
                  });
                if (
                  field.key === "city" &&
                  event.target.value.trim().length >= LOCATION_SEARCH_MIN_LENGTH
                )
                  setQuery(event.target.value.trim());
              }}
              onBlur={() => {
                if (field.key !== "city") return;
                closeSuggestions();
                if (!resolving) setStatus("");
              }}
              onKeyDown={(event) => {
                if (field.key !== "city") return;
                if (event.key === "Escape" && (suggestions.length || status)) {
                  event.preventDefault();
                  event.stopPropagation();
                  dismiss();
                }
                if (!suggestions.length) return;
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  setActiveIndex((current) =>
                    event.key === "ArrowDown"
                      ? (current + 1) % suggestions.length
                      : current <= 0
                        ? suggestions.length - 1
                        : current - 1,
                  );
                }
                if (event.key === "Enter" && activeIndex >= 0) {
                  event.preventDefault();
                  void select(suggestions[activeIndex]);
                }
              }}
            />
            {field.key === "city" && (suggestions.length > 0 || status) ? (
              <div
                data-slot="location-dropdown"
                className="absolute left-0 right-0 top-full z-20 mt-1 rounded-[10px] border border-[#D0D5DD] bg-white p-1 shadow-lg"
              >
                <ul
                  id={`${id}-suggestions`}
                  role="listbox"
                  aria-label="Location suggestions"
                  className="max-h-48 overflow-y-auto overscroll-contain"
                >
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={suggestion.id}
                      id={`${id}-option-${index}`}
                      role="option"
                      aria-selected={activeIndex === index}
                      className={cn(
                        "cursor-pointer rounded-md px-3 py-2 text-sm text-[#101828] hover:bg-[#F2F4F7]",
                        activeIndex === index && "bg-[#F2F4F7]",
                      )}
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={() => void select(suggestion)}
                    >
                      {suggestion.label}
                    </li>
                  ))}
                </ul>
                {status ? (
                  <p
                    role="status"
                    className="flex items-center gap-2 px-3 py-3 text-sm text-[#667085]"
                  >
                    {resolving || status === LOCATION_SEARCHING_MESSAGE ? (
                      <span
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#D0D5DD] border-t-[#667085]"
                      />
                    ) : null}
                    {status}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
